import { GeoJSON2SVG } from 'geojson2svg';
import { getMap, reverseMap, registerMap } from './maps';
import { Point, Options, GeoLevel, SVGMap } from './types';
import * as turf from '@turf/turf';

const defaultOptions: Options = {
    class: '',
    width: 400,
    height: 400,
    padding: 0,
    attributes: {},
    points: [],
};

function svgMapToString(svg: SVGSVGElement): string {
    const div = document.createElementNS('http://www.w3.org/1999/xhtml', 'div');

    div.appendChild(svg);

    let data = div.innerHTML;

    // Add namespace if missing
    if (data.indexOf(`http://www.w3.org/2000/svg`) < 0) {
        data = data.replace(/<svg/g, `<svg xmlns="http://www.w3.org/2000/svg"`);
    }

    // Return with all single quotes replace to double quotes
    return data.replace(/'/g, `"`);
}

export function SVGMapToString(countryid: string, options: Options): string {
    const svg = SVGMap(countryid, options);
    if (svg === null) {
        return '';
    }

    return svgMapToString(svg);
}

export function ReverseSVGMapToString(level: GeoLevel, lat: number, lng: number, options: Options): string {
    const svg = ReverseSVGMap(level, lat, lng, options);
    if (svg === null) {
        return '';
    }

    return svgMapToString(svg);
}

export function SVGMapToDOM(id: string, countryid: string, options: Options): boolean {
    const elm = document.getElementById(id);
    if (elm === null) {
        console.warn('element with ID', id, 'not found');
        return false;
    }

    while (elm.firstElementChild) {
        elm.firstElementChild.remove();
    }

    const svg = SVGMap(countryid, options);
    if (svg === null) {
        return false;
    }

    elm.appendChild(svg);

    return true;
}

export function ReverseSVGMapToDOM(id: string, level: GeoLevel, lat: number, lng: number, options: Options): boolean {
    const elm = document.getElementById(id);
    if (elm === null) {
        console.warn('element with ID', id, 'not found');
        return false;
    }

    while (elm.firstElementChild) {
        elm.firstElementChild.remove();
    }

    const svg = ReverseSVGMap(level, lat, lng, options);
    if (svg === null) {
        return false;
    }

    elm.appendChild(svg);

    return true;
}

export function ReverseSVGMap(level: GeoLevel, lat: number, lng: number, options: Options): SVGSVGElement | null {
    let data: any = null;

    switch (level) {
        case GeoLevel.Feature:
            data = reverseMap(GeoLevel.Feature, lat, lng);
            if (data !== null) {
                break;
            }
        case GeoLevel.Subcountry:
            data = reverseMap(GeoLevel.Subcountry, lat, lng);
            if (data !== null) {
                break;
            }
        case GeoLevel.Country:
            data = reverseMap(GeoLevel.Country, lat, lng);
            if (data !== null) {
                break;
            }
        case GeoLevel.World:
            data = reverseMap(GeoLevel.World, lat, lng);
            break;
    }

    if (data === null) {
        console.warn('map for', level, 'at', lat, lng, 'not found');
        return null;
    }

    return createSVGMap(data, options);
}

export function SVGMap(countryid: string, options: Options): SVGSVGElement | null {
    let data: any = null;

    countryid = countryid.toLowerCase();

    if (countryid === '' || countryid === 'world') {
        countryid = 'world';
        data = getMap(GeoLevel.World, 'world');
    } else {
        data = getMap(GeoLevel.Country, countryid);
        if (data === null) {
            data = getMap(GeoLevel.Feature, countryid);
        }
    }

    if (data === null) {
        console.warn('map for', countryid, 'not found');
        return null;
    }

    return createSVGMap(data, options);
}

function createSVGMap(data: any, options: Options): SVGSVGElement {
    options = {
        ...defaultOptions,
        ...options,
    };

    let points: Point[] = [];
    if (options.points) {
        points = options.points;
    }

    if (data.type === 'Feature') {
        data = {
            type: 'FeatureCollection',
            features: [data],
            properties: data.properties,
        };
    }

    let bbox = [-180, -85, 180, 85];
    if (data.properties.type !== 'world') {
        bbox = turf.bbox(data, { recompute: true });
    }

    //console.log('bbox', bbox);

    if (data.type === 'FeatureCollection') {
        for (const point of points) {
            data.features.push({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [point.center[0], point.center[1]],
                },
                properties: {
                    name: 'The point',
                },
            });
        }
    }

    const kwidth = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[2], bbox[1]]), { units: 'kilometers' });
    const kheight = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[0], bbox[3]]), { units: 'kilometers' });

    //console.log('twidth', kwidth.toFixed(3), 'km', 'theight', kheight.toFixed(3), 'km');

    let aspect = kwidth / kheight;
    if (data.properties.type === 'world') {
        aspect = 1 / 1;
    }

    //console.log('aspect', aspect);

    let fitTo = 'width';
    let factor = 1;
    if (aspect < 1) {
        fitTo = 'height';
        factor = 1 / aspect;
    }

    const converter = new GeoJSON2SVG({
        mapExtent: { left: bbox[0], bottom: bbox[1], right: bbox[2], top: bbox[3] },
        viewportSize: { width: options.width, height: options.width / aspect },
        fitTo: fitTo === 'width' ? 'width' : 'height',
        coordinateConverter: turf.toMercator,
        attributes: options.attributes,
        pointAsCircle: true,
    });
    const svgString = converter.convert(data);

    const div = document.createElementNS('http://www.w3.org/1999/xhtml', 'div');
    div.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg">' + svgString.join('') + '</svg>';
    const frag = document.createDocumentFragment();

    let ncircles = 0;
    while (div.firstChild?.firstChild) {
        const node = div.firstChild.firstChild as SVGElement;

        if (node.nodeName === 'circle' && ncircles < points.length) {
            const point = points[ncircles];

            node.removeAttribute('fill');
            node.removeAttribute('r');

            for (const key in point.attributes) {
                node.setAttribute(key, point.attributes[key]);
            }

            let val = node.getAttribute('r');
            if (val !== null) {
                node.setAttribute('r', '' + parseFloat(val) * factor);
            }

            val = node.getAttribute('stroke-width');
            if (val !== null) {
                node.setAttribute('stroke-width', '' + parseFloat(val) * factor);
            }

            ncircles++;
        }

        frag.appendChild(node);
    }

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    if (options.class) {
        svg.setAttribute('class', options.class);
    }

    let viewBox = '0 0 ' + options.width + ' ' + options.width / aspect;
    if (options.padding && options.padding != 0) {
        viewBox = -1 * options.padding + ' ' + -1 * options.padding + ' ' + (options.width + 2 * options.padding) + ' ' + (options.width / aspect + 2 * options.padding);
    }

    svg.setAttribute('viewBox', viewBox);

    svg.appendChild(frag);

    return svg;
}

export function RegisterSVGMap(level: GeoLevel, name: string, map: SVGMap) {
    registerMap(level, name, map);
}

declare global {
    interface Window {
        SVGMapToDOM: any;
        SVGMapToString: any;
        SVGMap: any;
        ReverseSVGMapToDOM: any;
        ReverseSVGMapToString: any;
        ReverseSVGMap: any;
        RegisterSVGMap: any;
    }
}

if (typeof window !== 'undefined') {
    window.SVGMapToDOM = SVGMapToDOM;
    window.SVGMapToString = SVGMapToString;
    window.SVGMap = SVGMap;
    window.ReverseSVGMapToString = ReverseSVGMapToString;
    window.ReverseSVGMapToDOM = ReverseSVGMapToDOM;
    window.ReverseSVGMap = ReverseSVGMap;
    window.RegisterSVGMap = RegisterSVGMap;
}
